/**
 * generationEstimate — «What you get» arithmetic for the create wizard's review
 * step (W12 Part B, Task B6).
 *
 * Pure: routes in, screens and dollars out. No React, no services, no I/O.
 *
 * Honesty rules this module exists to enforce:
 * - every number it produces is labelled *estimated* by its caller and is
 *   derived from a MEASURED reference, never from a guess;
 * - anything that cannot be computed honestly comes back `null`, so the UI
 *   renders «—» plus the reason instead of inventing a promise;
 * - it never produces a DURATION. How long a generation takes is not measured,
 *   so no surface may claim it (strategy-blocks designer invariant 22).
 */

/**
 * The measured reference point of the estimate.
 *
 * MEASURED, not guessed: the W10 accepted generation (SPEND-LEDGER.md row D-11,
 * 2026-09-08 — W10 R4, 19 blocks / 77 screens over 3 task routes, billed
 * `billing.customerAmountMinor` 344 = $3.44 on `gemini-3.8-flash`). That gives
 * ≈25 screens per route and ≈$0.045 per screen (plan + screens) at an output
 * price of $3.75 per 1M tokens; the cost scales linearly with the chosen
 * model's output price.
 *
 * Refine in Task A3 step 3 from the clean cycle's receipt, and cite the ledger
 * row here when you do.
 */
export const GENERATION_ESTIMATE_REFERENCE = {
  screensPerRoute: 25,
  usdPerScreenWithPlan: 0.045,
  referenceModel: 'gemini-3.8-flash',
  referenceOutputUsdPerMTokens: 3.75,
  source: 'SPEND-LEDGER.md D-11 (2026-09-08)',
} as const;

/**
 * A plan-only run (`generateScreens` off) produces the block plan and no
 * screens.
 *
 * NOT measured: D-11 is a with-screens run and there is no plan-only receipt to
 * derive this from. 15 % is a planning ASSUMPTION, refined in Task A3 step 3
 * once a plan-only cycle has a receipt. Everything the UI derives from it is
 * labelled *estimated*, and the assumption is named here rather than hidden
 * inside the formula.
 */
export const PLAN_ONLY_COST_SHARE = 0.15;

/** The one reason both estimate rows can be empty. */
export const ESTIMATE_UNAVAILABLE_DETAIL = 'select at least one task to estimate';

export interface GenerationEstimate {
  /** Screens the run would produce. `null` when there is no route to count. */
  screens: number | null;
  /** Dollars, rounded to cents. `null` without routes OR without a model price. */
  usd: number | null;
}

export interface EstimateGenerationArgs {
  /** Task routes in the request — the same count the readiness rows report. */
  routes: number;
  /** Whether screens are generated together with the block plan. */
  generateScreens: boolean;
  /**
   * Output price of the SELECTED model, per 1M tokens. Omitted when the model
   * is not in the project's catalog — the cost then reads «—», never a number
   * borrowed from another model.
   */
  modelOutputUsdPerMTokens?: number;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

export function estimateGeneration(args: EstimateGenerationArgs): GenerationEstimate {
  const { routes, generateScreens, modelOutputUsdPerMTokens } = args;
  if (!Number.isFinite(routes) || routes <= 0) return { screens: null, usd: null };

  const screens = Math.round(routes) * GENERATION_ESTIMATE_REFERENCE.screensPerRoute;
  if (modelOutputUsdPerMTokens === undefined || !Number.isFinite(modelOutputUsdPerMTokens)) {
    return { screens, usd: null };
  }

  const priceRatio = modelOutputUsdPerMTokens
    / GENERATION_ESTIMATE_REFERENCE.referenceOutputUsdPerMTokens;
  const full = screens * GENERATION_ESTIMATE_REFERENCE.usdPerScreenWithPlan * priceRatio;
  return { screens, usd: round2(generateScreens ? full : full * PLAN_ONLY_COST_SHARE) };
}

/**
 * The two VALUE strings of the estimate rows. The detail lines (which name the
 * model, its prices and the word «estimated») belong to the review step, which
 * knows them; this keeps the module free of copy it cannot compute.
 */
export const formatEstimate = (
  estimate: GenerationEstimate,
  routes: number,
): { screens: string; cost: string } => ({
  screens: estimate.screens === null
    ? '—'
    : `≈ ${estimate.screens} screens across ${routes} route${routes === 1 ? '' : 's'}`,
  cost: estimate.usd === null ? '—' : `≈ $${estimate.usd.toFixed(2)}`,
});

/** Per-1M-token prices of one model, as the UI needs them. */
export interface ModelPricing {
  /**
   * How the model is named in one rail caption or one review row: the
   * catalog's `displayName` when it has one, else the model id after its last
   * `/` (provider-prefixed ids such as `google/gemini-3-flash`).
   */
  shortName: string;
  inputUsdPerMTokens?: number;
  outputUsdPerMTokens?: number;
}

/** The shape `useAvailableAIModels().textModels` entries carry (ai-settings). */
export interface PricedModel {
  id: string;
  displayName?: string;
  /** Catalog prices are per 1 000 tokens; the UI speaks in millions. */
  costPer1kInput?: number;
  costPer1kOutput?: number;
}

const perMTokens = (costPer1k: number | undefined): number | undefined =>
  (typeof costPer1k === 'number' && Number.isFinite(costPer1k) ? costPer1k * 1000 : undefined);

/**
 * Resolves the selected model to its per-1M prices. A model the project's
 * catalog does not carry yields prices of `undefined` (and therefore a `null`
 * cost) — the id still names the model so the UI never reads as «no model».
 */
export function resolveModelPricing(
  models: ReadonlyArray<PricedModel>,
  modelId: string,
): ModelPricing {
  const fallbackName = modelId ? modelId.split('/').pop() ?? modelId : '';
  const model = models.find((candidate) => candidate.id === modelId);
  if (!model) return { shortName: fallbackName };
  return {
    shortName: model.displayName || fallbackName,
    inputUsdPerMTokens: perMTokens(model.costPer1kInput),
    outputUsdPerMTokens: perMTokens(model.costPer1kOutput),
  };
}
